import { MM_PER_INCH } from '@frontend/partviewer/src/constants';

import { UNITS } from 'constants/all';
import { UNITS_OF_MEASURE } from 'constants/unitOfMeasure';

import { formatFloatIncludingZero } from 'utils/formatters';

import type { DisplayUnits } from '../session/sceneUnits';

export type MeasurementFormat = {
  /** Multiplier from drawing units to `displayUnits`. */
  scale: number;
  /** Unit values are presented in. */
  displayUnits: DisplayUnits;
  /** Decimal places, 0–6. */
  precision: number;
  /**
   * True when resolved scene units are unknown / unitless. Values are then shown
   * raw with a neutral "u" suffix — converting a number whose unit nobody knows
   * would be a lie.
   */
  isUnitless: boolean;
};

/** Converts between the two units the viewer formats in. */
const convertLength = (
  value: number,
  from: DisplayUnits,
  to: DisplayUnits,
): number => {
  if (from === to) {
    return value;
  }
  // Exact factor (no helper rounding) — this builds a scale used on every sample.
  return from === UNITS.IN ? value * MM_PER_INCH : value / MM_PER_INCH;
};

/**
 * Builds the conversion from a drawing's native unit to what the user wants to see.
 *
 * Two steps folded into one multiplier: `PartFile.dxfSceneState.units` says what a
 * drawing coordinate means, and the user's preference says what to show it as.
 */
export const measurementFormat = ({
  nativeUnits,
  nativeScale,
  isUnitless,
  displayUnits,
  precision,
}: {
  nativeUnits: DisplayUnits | null;
  nativeScale: number;
  isUnitless: boolean;
  displayUnits: DisplayUnits;
  precision: number;
}): MeasurementFormat => ({
  scale: isUnitless
    ? 1
    : convertLength(nativeScale, nativeUnits ?? displayUnits, displayUnits),
  displayUnits,
  precision,
  isUnitless,
});

/** Suffix for a length: shared unit label, or a neutral marker when unitless. */
export const lengthSuffix = ({
  displayUnits,
  isUnitless,
}: MeasurementFormat): string =>
  isUnitless ? 'u' : UNITS_OF_MEASURE[displayUnits];

/** Suffix for an area. Squared units from the shared measure table. */
export const areaSuffix = ({
  displayUnits,
  isUnitless,
}: MeasurementFormat): string => {
  if (isUnitless) {
    return 'u²';
  }
  return displayUnits === UNITS.IN
    ? UNITS_OF_MEASURE.squareIn
    : UNITS_OF_MEASURE.squareMm;
};

const round = (value: number, precision: number): string =>
  formatFloatIncludingZero(value, Math.max(0, Math.min(6, precision)));

const NBSP = '\u202F';

/** Formats a length given in drawing units. */
export const formatLength = (
  value: number,
  format: MeasurementFormat,
): string =>
  `${round(value * format.scale, format.precision)}${NBSP}${lengthSuffix(
    format,
  )}`;

/** Formats an area given in squared drawing units. */
export const formatArea = (value: number, format: MeasurementFormat): string =>
  `${round(
    value * format.scale * format.scale,
    format.precision,
  )}${NBSP}${areaSuffix(format)}`;

/** Formats an angle given in radians, in degrees as the legacy viewer does. */
export const formatAngle = (
  radians: number,
  format: MeasurementFormat,
): string => {
  const degrees = (radians * 180) / Math.PI;
  const places = Math.max(0, Math.min(6, format.precision));
  return `${Number(degrees.toFixed(places))}${NBSP}deg`;
};

/** Formats a width × height pair, sharing one unit suffix. */
export const formatSize = (
  width: number,
  height: number,
  format: MeasurementFormat,
): string =>
  `${round(width * format.scale, format.precision)} × ${round(
    height * format.scale,
    format.precision,
  )}${NBSP}${lengthSuffix(format)}`;
